import React from "react";
import dayjs from "dayjs";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
  Checkbox,
  Box,
  TablePagination,
} from "@mui/material";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { getFormSortLogsApi } from "../../../api/userApi/userApi";
import useEncryption from "../../../services/useEncryption";
import { useDispatch } from "react-redux";
import { setSelectedLogs } from "../../../redux/slice/locSlice";
import { useOutletContext } from "react-router-dom";
import { Button } from "@mui/material";

export const AdminFormSortLogs = ({ search, setSearch }) => {
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [count, setCount] = React.useState(0);
  const [selected, setSelected] = React.useState([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedRowData, setSelectedRowData] = React.useState(null);

  const dispatch = useDispatch();
  const [loading, setLoading] = useOutletContext();
  const { decryptData } = useEncryption();

  React.useEffect(() => {
    fetchLogs();
  }, [page, rowsPerPage, search]);

  const exportToCsv = () => {
    if (!rows || rows.length === 0) return;
  
    const safeRows = rows || [];
    const safeSelected = selected || [];
  
    // Filter selected rows ONLY
    const filteredRows =
      safeSelected.length > 0
        ? safeRows.filter((r) => safeSelected.includes(r._id))
        : safeRows;
  
    if (!filteredRows.length) return;
  
    let csvRows = [];
    const headers = [
      "Date & Time",
      "Full Name",
      "Age",
      "Age Category",
      "Zip Code",
      "Country",
      "Coordinates",
      "Fetched Providers",
      "Top Doctors Count",
      "Doctor Location Id",
      "Doctor Location Name",
      "Doctor Name",
      "Preferred Doctor Name",
      "Doctor Email",
      "Doctor Phone",
      "Doctor Distance"
    ];
  
    csvRows.push(headers.join(","));
  
    filteredRows.forEach((row) => {
      const dateTime = dayjs(row.createdAt).format("MM/DD/YY HH:mm:ss");
      const fullName = `${row.userData?.firstName ?? ""} ${row.userData?.lastName ?? ""}`.trim();
      const age = row.userData?.age ?? "";
      const ageCategory = row.userData?.ageCategory ?? "";
      const zip = row.zipCode ?? "";
      const country = row.countryOfResidence ?? "";
      const coords =
        row.userCoordinates?.latitude && row.userCoordinates?.longitude
          ? `${row.userCoordinates.latitude}, ${row.userCoordinates.longitude}`
          : "";
      const fetchedCnt = row.fetchedCount ?? 0;
      const topDoctorsCnt = row.topDoctorsCount ?? 0;
  
      if (!row.topDoctors || row.topDoctors.length === 0) {
        csvRows.push(
          [
            dateTime,
            fullName,
            age,
            ageCategory,
            zip,
            country,
            coords,
            fetchedCnt,
            topDoctorsCnt,
            "", "", "", "", "", "", ""
          ].map((v) => `"${v}"`).join(",")
        );
      } else {
        row.topDoctors.forEach((doc) => {
          const d = doc?.doctorId ?? {};
          const rowData = [
            dateTime,
            fullName,
            age,
            ageCategory,
            zip,
            country,
            coords,
            fetchedCnt,
            topDoctorsCnt,
            doc.locationId ?? "",
            doc.providerName ?? "",
            `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim(),
            d.doctorPreferredName ?? "",
            d.email ?? "",
            d.phoneNumber ?? "",
            doc.distance ?? ""
          ];
  
          csvRows.push(rowData.map((v) => `"${v}"`).join(","));
        });
      }
    });
  
    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "formsort_logs.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await getFormSortLogsApi(page, rowsPerPage, search);

      const decrypted = decryptData(response.data.logs);

      setRows(decrypted);
      setCount(response.data.totalCount);

      setSelected([]);
      dispatch(setSelectedLogs([]));

      setLoading(false);
    } catch (error) {
      console.error(error);
      setRows([]);
      setCount(0);
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = rows.map((r) => r._id);
      setSelected(allIds);
      dispatch(setSelectedLogs(allIds));
      return;
    }
    setSelected([]);
    dispatch(setSelectedLogs([]));
  };

  const handleRowSelect = (id) => {
    let newSelected = selected.includes(id)
      ? selected.filter((s) => s !== id)
      : [...selected, id];

    setSelected(newSelected);
    dispatch(setSelectedLogs(newSelected));
  };

  return (
    <div className="tableDesigned">
      <div className="d-flex align-items-center justify-content-between">
        <div className="searchBar">
          <input
            type="text"
            autoComplete="off"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <button className="btn blackoutlineBtn" onClick={exportToCsv}>
          Download CSV
        </button>
      </div>
      <Box>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selected.length > 0 && selected.length === rows.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Zip Code</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell>Fetched Providers</TableCell>
                  <TableCell>Top Doctors</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row._id}
                    hover
                    onClick={() => {
                      setSelectedRowData(row);
                      setOpenModal(true);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(row._id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleRowSelect(row._id)}
                      />
                    </TableCell>

                    <TableCell>
                      {dayjs(row.createdAt).format("MM/DD/YY HH:mm:ss")}
                    </TableCell>

                    <TableCell>{row.zipCode}</TableCell>
                    <TableCell>{row.countryOfResidence}</TableCell>
                    <TableCell>{row.userData?.firstName}</TableCell>
                    <TableCell>{row.userData?.age}</TableCell>
                    <TableCell>{row.fetchedCount}</TableCell>
                    <TableCell>{row.topDoctorsCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(0);
            }}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "none",
            }}
          />
        </Paper>
      </Box>
      {selectedRowData && (
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", sm: 600 },
              maxHeight: "80vh",
              overflowY: "auto",
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              p: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 10,
                padding: "16px 20px",
                borderBottom: "1px solid #ddd",
              }}
            >
              <Box component="h2" sx={{ m: 0, fontSize: 20 }}>
                Formsort Log Details
              </Box>

              <Box>
                <CloseIcon
                  onClick={() => setOpenModal(false)}
                  style={{ cursor: "pointer", fontSize: 26 }}
                />
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box mb={2}>
                <h3 style={{ marginBottom: "10px" }}>User Details</h3>
                <div>
                  <strong>Date & Time:</strong>{" "}
                  {dayjs(selectedRowData?.createdAt).format(
                    "MM/DD/YY HH:mm:ss"
                  )}
                </div>
                <div>
                  <strong>Full Name:</strong>{" "}
                  {(selectedRowData?.userData?.firstName ?? "") +
                    " " +
                    (selectedRowData?.userData?.lastName ?? "")}
                </div>
                <div>
                  <strong>Age:</strong>{" "}
                  {selectedRowData?.userData?.age ?? "N/A"}
                </div>
                <div>
                  <strong>Age Category:</strong>{" "}
                  {selectedRowData?.userData?.ageCategory ?? "N/A"}
                </div>
                <div>
                  <strong>Zip Code:</strong> {selectedRowData?.zipCode ?? "N/A"}
                </div>
                <div>
                  <strong>Country:</strong>{" "}
                  {selectedRowData?.countryOfResidence ?? "N/A"}
                </div>
                <div>
                  <strong>Coordinates (Lat, Lng):</strong>{" "}
                  {selectedRowData?.userCoordinates?.latitude &&
                  selectedRowData?.userCoordinates?.longitude
                    ? `${selectedRowData.userCoordinates.latitude}, ${selectedRowData.userCoordinates.longitude}`
                    : "N/A"}
                </div>

                <div>
                  <strong>Fetched Providers:</strong>{" "}
                  {selectedRowData?.fetchedCount ?? 0}
                </div>
                <div>
                  <strong>Top Doctors:</strong>{" "}
                  {selectedRowData?.topDoctorsCount ?? 0}
                </div>
              </Box>

              <div style={{ marginTop: "20px" }}>
                <h3 style={{ marginBottom: "10px" }}>Top Doctors Details</h3>

                {selectedRowData?.topDoctors?.length > 0 ? (
                  selectedRowData.topDoctors.map((doc, index) => {
                    const d = doc?.doctorId;

                    const fullName =
                      `${d?.firstName ?? ""} ${d?.lastName ?? ""}`.trim() ||
                      d?.doctorPreferredName ||
                      "N/A";

                    return (
                      <div key={doc._id} style={{ marginBottom: "15px" }}>
                        <div>
                          <strong>Location Id:</strong>{" "}
                          {doc?.locationId ?? "N/A"}
                        </div>
                        <div>
                          <strong>Location Name:</strong>{" "}
                          {doc?.providerName ?? "N/A"}
                        </div>

                        <div>
                          <strong>Doctor Name:</strong> {fullName}
                        </div>

                        <div>
                          <strong>Preferred Doctor Name:</strong>{" "}
                          {d?.doctorPreferredName ?? "N/A"}
                        </div>

                        <div>
                          <strong>Email:</strong> {d?.email ?? "N/A"}
                        </div>

                        <div>
                          <strong>Phone Number:</strong>{" "}
                          {d?.phoneNumber ?? "N/A"}
                        </div>

                        <div>
                          <strong>Distance:</strong> {doc?.distance ?? "N/A"}
                        </div>

                        <hr />
                      </div>
                    );
                  })
                ) : (
                  <div>No Top Doctors Found</div>
                )}
              </div>
            </Box>
          </Box>
        </Modal>
      )}
    </div>
  );
};
